// L'album dei ricordi: una raccolta di pagine con la sua copertina, da sfogliare.
//
// Tre scelte da cui dipende tutto il resto:
//  1. Una pagina = una storia, non due facciate affiancate. A 320 px con il testo molto grande
//     due colonne non ci stanno, e mamma legge una cosa per volta.
//  2. Si sfoglia con due pulsanti grandi, non con lo swipe: sull'iPad lo scorrimento dal bordo
//     è il «indietro» di Safari, e glielo toglieremmo di mano.
//  3. Sfogliare NON aggiunge voci alla cronologia (`history.replaceState`): «Indietro» vuol
//     dire sempre «esci dall'album», non «una pagina indietro venti volte».
import { api } from '../lib/api.js';
import { pageEditor, type PageStatus } from '../lib/page-editor.js';
import { el, fmtDate } from '../lib/ui.js';
import { figuraVideo, type VideoPagina } from '../lib/video.js';

type UI = {
  screen: (...nodes: Array<Node | string | null | undefined>) => void;
  testata: (title: string) => HTMLElement;
  speak: (text: string, opts?: { force?: boolean }) => Promise<void>;
};

export type FotoPagina = { id: number; didascalia: string | null };
export type CapitoloPagina = { chiave: string; titolo: string; emoji: string | null };
export type PaginaAlbum = {
  storiaId: number; titolo: string; data: string | null; anno: number | null;
  testo: string; foto: FotoPagina[]; capitolo: CapitoloPagina | null;
  /** I video pronti, dopo le foto (assente sui server più vecchi). */
  video?: VideoPagina[];
  disegno?: { url: string; didascalia: string | null } | null;
};
export type Album = {
  tipo: string;
  chiave: string;
  titolo: string;
  emoji: string | null;
  introduzione: string | null;
  riassunto: PageStatus | null;
  copertina: FotoPagina | null;
  pagine: PaginaAlbum[];
  prima: { chiave: string; titolo: string } | null;
  dopo: { chiave: string; titolo: string } | null;
};

/** La larghezza chiesta al server: le foto originali pesano anche 6 MB l'una. */
const LARGHEZZA_PAGINA = 900;
const LARGHEZZA_COPERTINA = 900;

const indirizzo = (a: { tipo: string; chiave: string }, n: number): string =>
  `#album/${a.tipo}/${a.chiave}${n > 0 ? `/${n}` : ''}`;

/** Cambia l'indirizzo senza aggiungere una voce alla cronologia né far ripartire il router. */
function segnaPagina(a: Album, n: number): void {
  history.replaceState(history.state, '', indirizzo(a, n));
}

function paragrafi(testo: string): HTMLElement[] {
  return testo.split(/\n+/).filter(Boolean).map((par) => el('p', {}, par));
}

/** Una fotografia della pagina, nel suo passe-partout, con la didascalia sotto.
 *  La prima si carica subito: è quella che mamma vede aprendo la pagina. */
function lastra(f: FotoPagina, larghezza: number, prima: boolean): HTMLElement {
  return el('figure', { class: 'album-figura' },
    el('img', {
      class: 'album-foto',
      src: `/api/media/${f.id}?w=${larghezza}`,
      alt: f.didascalia ?? '',
      loading: prima ? 'eager' : 'lazy',
      decoding: 'async',
    }),
    f.didascalia ? el('figcaption', {}, f.didascalia) : null,
  );
}

export async function renderAlbum(ui: UI, tipo: string, chiave: string, pagina: number): Promise<void> {
  const a = await api.get<Album>(`/api/album/${tipo}/${chiave}`);
  const totale = a.pagine.length;
  // 0 = copertina; da 1 a N le pagine. Un numero fuori misura si riporta dentro, senza errori.
  let n = Number.isFinite(pagina) ? Math.min(Math.max(Math.trunc(pagina), 0), totale) : 0;

  const disegna = (): void => {
    segnaPagina(a, n);
    ui.screen(ui.testata(`${a.emoji ?? '📖'} ${a.titolo}`), n === 0 ? copertina() : facciata(a.pagine[n - 1]!));
  };

  // `screen()` riporta già in cima e rimonta le sorprese: qui basta ridisegnare.
  const vaiA = (dove: number): void => { n = dove; disegna(); };

  // Un decennio NON è un capitolo del libro: i capitoli sono gli argomenti, e «gli anni
  // Cinquanta» nell'indice del Libro non compaiono. Dirglielo sarebbe mandarla a cercare una
  // cosa che non c'è.
  const occhiello = a.tipo === 'decennio' ? 'UN PEZZO DELLA TUA STORIA' : 'UN CAPITOLO DEL TUO LIBRO';
  const doveSiTorna: [string, string] = a.tipo === 'decennio'
    ? ['#cronologia', 'Torna alla tua storia nel tempo']
    : ['#libro', 'Torna all’indice del libro'];

  /** La copertina: la prima foto vera del capitolo, mai un disegno finto al suo posto. */
  const copertina = (): HTMLElement => {
    const conta = `${totale} pagin${totale === 1 ? 'a' : 'e'}`;
    return el('div', { class: 'album' },
      el('section', { class: 'album-copertina' + (a.copertina ? '' : ' senza-foto'), 'aria-label': `Copertina di ${a.titolo}` },
        a.copertina ? el('img', { class: 'album-copertina-foto', src: `/api/media/${a.copertina.id}?w=${LARGHEZZA_COPERTINA}`, alt: '', decoding: 'async' }) : null,
        el('div', { class: 'album-copertina-testo' },
          el('p', { class: 'eyebrow' }, occhiello),
          el('p', { class: 'album-emoji', 'aria-hidden': 'true' }, a.emoji ?? '📖'),
          el('h2', {}, a.titolo),
          el('p', { class: 'album-conta' }, conta),
        ),
      ),
      a.introduzione ? el('div', { class: 'album-introduzione' }, ...paragrafi(a.introduzione)) : null,
      // Il riassunto del capitolo si corregge da qui. Stava in `#leggi/:slug`, che oggi è
      // l'album: senza questo, l'unica porta per cambiare le parole di un capitolo si era
      // chiusa in silenzio, e `services/page-edits.ts` restava raggiungibile solo dall'admin.
      a.tipo === 'capitolo'
        ? pageEditor('theme', a.chiave, a.introduzione, a.riassunto ?? undefined, () => renderAlbum(ui, tipo, chiave, 0))
        : null,
      totale === 0
        ? el('div', {},
            el('p', { class: 'vuoto' }, a.tipo === 'decennio'
              ? 'Di questi anni non hai ancora raccontato niente.'
              : 'Questo capitolo aspetta ancora la sua prima storia.'),
            viaDUscita())
        : el('button', { class: 'btn-primario album-apri', click: () => vaiA(1) }, 'Apri l’album  ›'),
      piedeCopertina(),
    );
  };

    /** Una storia senza fotografie non ne riceve una finta: prende una fascia di carta con
   *  l'emoji del suo capitolo e il decennio, e la sua prima frase in grande al posto della
   *  didascalia. È il posto dove chiederle, con garbo, quando è successo. */
  const fascia = (p: PaginaAlbum): HTMLElement => {
    const decennio = p.anno !== null ? `Anni ${Math.floor(p.anno / 10) * 10}` : null;
    const primaFrase = (p.testo.match(/^[^.!?\n]{10,140}[.!?]?/) ?? [])[0]?.trim();
    return el('div', { class: 'album-fascia' },
      el('p', { class: 'album-fascia-emoji', 'aria-hidden': 'true' }, p.capitolo?.emoji ?? '📖'),
      el('p', { class: 'album-fascia-dove' }, [p.capitolo?.titolo, decennio].filter(Boolean).join(' · ') || 'Un ricordo'),
      primaFrase ? el('p', { class: 'album-fascia-frase' }, primaFrase) : null,
    );
  };

  /** Una pagina: la data, il titolo, le fotografie grandi con le didascalie, i video, il racconto.
   *  Il disegno e la fascia di carta restano per le pagine che non hanno né foto né video. */
  const facciata = (p: PaginaAlbum, video = p.video ?? []): HTMLElement => el('div', { class: 'album' },
    el('article', { class: 'album-pagina' + (p.foto.length === 0 && video.length === 0 ? ' senza-foto' : ''), 'aria-label': `Pagina ${n} di ${totale}` },
      p.data
        ? el('p', { class: 'album-data' }, fmtDate(p.data))
        : el('button', {
            class: 'album-quando',
            click: () => { location.hash = `#chiedi?storia=${p.storiaId}&chiedi=quando`; },
          }, 'Sai dirmi quando? ›'),
      el('h2', { class: 'album-titolo' }, p.titolo),
      ...p.foto.map((f, i) => lastra(f, LARGHEZZA_PAGINA, i === 0)),
      ...video.map((v) => figuraVideo(v, p.titolo)),
      ...(p.foto.length || video.length
        ? []
        : p.disegno
          // Senza fotografie, il disegno della storia illustra la pagina (mai la copertina).
          ? [el('figure', { class: 'album-disegno' },
              el('img', { src: p.disegno.url, alt: p.disegno.didascalia ? `Disegno: ${p.disegno.didascalia}` : 'Un disegno della storia', loading: 'lazy', decoding: 'async' }),
              p.disegno.didascalia ? el('figcaption', {}, p.disegno.didascalia) : null)]
          : [fascia(p)]),
      el('div', { class: 'album-testo' }, ...paragrafi(p.testo)),
      el('p', { class: 'album-numero', 'aria-hidden': 'true' }, String(n)),
    ),
    el('p', { class: 'album-dove', role: 'status', 'aria-live': 'polite' }, `Pagina ${n} di ${totale}`),
    el('div', { class: 'album-sfoglia' }, indietro(), avanti()),
    el('div', { class: 'album-attrezzi' },
      p.testo ? el('button', { class: 'btn-quieto', click: () => void ui.speak(`${p.titolo}. ${p.testo}`, { force: true }) }, '🔊  Leggimi questa pagina') : null,
      el('button', { class: 'btn-quieto', click: () => { location.hash = `#storia/${p.storiaId}`; } }, 'Apri o correggi questa storia'),
    ),
  );

  const indietro = (): HTMLElement => n <= 1
    ? el('button', { class: 'btn-secondario album-passo', click: () => vaiA(0) }, '‹  La copertina')
    : el('button', { class: 'btn-secondario album-passo', click: () => vaiA(n - 1) }, '‹  Pagina prima');

  const avanti = (): HTMLElement => n < totale
    ? el('button', { class: 'btn-primario album-passo', click: () => vaiA(n + 1) }, 'Pagina dopo  ›')
    : a.dopo
      ? el('button', { class: 'btn-primario album-passo', click: () => { location.hash = `#album/${a.tipo}/${a.dopo!.chiave}`; } }, `Capitolo dopo: «${a.dopo.titolo}»  ›`)
      : el('button', { class: 'btn-secondario album-passo', click: () => { location.hash = doveSiTorna[0]; } }, doveSiTorna[1]);

  /** Dalla copertina si passa ai capitoli vicini, come sfogliando il libro da fermi. */
  const piedeCopertina = (): HTMLElement => el('div', { class: 'album-sfoglia album-sfoglia-copertina' },
    a.prima
      ? el('button', { class: 'btn-secondario album-passo', click: () => { location.hash = `#album/${a.tipo}/${a.prima!.chiave}`; } }, `‹  «${a.prima.titolo}»`)
      : el('span', {}),
    a.dopo
      ? el('button', { class: 'btn-secondario album-passo', click: () => { location.hash = `#album/${a.tipo}/${a.dopo!.chiave}`; } }, `«${a.dopo.titolo}»  ›`)
      : el('span', {}),
  );

  /** Un capitolo senza nemmeno una pagina non deve essere un vicolo cieco. */
  const viaDUscita = (): HTMLElement =>
    el('button', { class: 'btn-secondario album-passo', click: () => { location.hash = doveSiTorna[0]; } }, doveSiTorna[1]);

  disegna();
}
